import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import {
  closeCart,
  removeItem,
  updateQuantity,
  selectCartItems,
  selectCartTotal,
  selectCartOpen,
} from "../../store/cartSlice";

const fmtPrice = (v) =>
  v?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * CartDropdown — slides in from top-right, anchored below the navbar.
 * Closes on outside click or Escape key.
 */
export default function CartDropdown() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const items = useSelector(selectCartItems);
  const total = useSelector(selectCartTotal);
  const isOpen = useSelector(selectCartOpen);
  const panelRef = useRef(null);

  /* Close on outside click */
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        dispatch(closeCart());
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, dispatch]);

  /* Close on Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") dispatch(closeCart()); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [dispatch]);

  if (!isOpen) return null;

  const handleViewCart = () => {
    dispatch(closeCart());
    navigate("/cart");
  };

  const handleCheckout = () => {
    dispatch(closeCart());
    navigate("/cart?checkout=1");
  };

  return (
    <>
      {/* Backdrop — only on mobile */}
      <div
        onClick={() => dispatch(closeCart())}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.3)",
          zIndex: 148,
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
        }}
        className="md:hidden"
      />

      {/* Dropdown panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Shopping cart"
        style={{
          position: "fixed",
          top: "var(--header-h, 72px)",
          right: 16,
          width: 380,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - var(--header-h, 72px) - 24px)",
          background: "#0a0a0f",
          borderRadius: 20,
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.5), 0 4px 16px rgba(0,0,0,0.3)",
          display: "flex",
          flexDirection: "column",
          zIndex: 149,
          animation: "fade-up 0.2s ease-out",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid hsl(var(--border))",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ShoppingBag size={17} style={{ color: "var(--vb-indigo)" }} />
            <span
              style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 600,
                fontSize: 16,
                color: "hsl(var(--foreground))",
              }}
            >
              Cart
            </span>
            {items.length > 0 && (
              <span
                style={{
                  background: "hsl(var(--secondary))",
                  color: "var(--vb-indigo)",
                  borderRadius: 9999,
                  padding: "1px 8px",
                  fontSize: 11,
                  fontFamily: "'DM Mono', monospace",
                  fontWeight: 600,
                }}
              >
                {items.reduce((s, i) => s + i.quantity, 0)}
              </span>
            )}
          </div>
          <button
            onClick={() => dispatch(closeCart())}
            aria-label="Close cart"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: 9999,
              border: "1px solid hsl(var(--border))",
              background: "transparent",
              cursor: "pointer",
              color: "hsl(var(--muted-foreground))",
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Items */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 0" }}>
          {items.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 24px",
                gap: 12,
                color: "hsl(var(--muted-foreground))",
              }}
            >
              <ShoppingBag size={36} style={{ opacity: 0.25 }} />
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14 }}>
                Your cart is empty
              </p>
              <button
                onClick={() => { dispatch(closeCart()); navigate("/shop"); }}
                style={{
                  background: "var(--vb-indigo)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "8px 18px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "'Inter', sans-serif",
                  marginTop: 4,
                }}
              >
                Browse Machines
              </button>
            </div>
          ) : (
            items.map(({ product, quantity, selectedColor }) => {
              const pid = product.mongo_id || product.id;
              const img = product.photo_urls?.[0] || product.img_url;
              const price = product.sale_price || 0;
              return (
                <div
                  key={pid}
                  style={{
                    display: "flex",
                    gap: 12,
                    padding: "10px 20px",
                    borderBottom: "1px solid hsl(var(--border) / 0.5)",
                  }}
                >
                  {/* Thumbnail */}
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 10,
                      overflow: "hidden",
                      flexShrink: 0,
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    {img ? (
                      <img
                        src={img}
                        alt={product.product_name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 9,
                          color: "#9d8fff",
                          fontFamily: "monospace",
                        }}
                      >
                        IMG
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "hsl(var(--foreground))",
                        lineHeight: 1.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        marginBottom: 4,
                      }}
                    >
                      {product.product_name}
                    </p>
                    {product.machine_type && (
                      <span
                        style={{
                          fontSize: 10,
                          fontFamily: "'DM Mono', monospace",
                          color: "var(--vb-indigo)",
                          background: "hsl(var(--secondary))",
                          borderRadius: 6,
                          padding: "1px 7px",
                        }}
                      >
                        {product.machine_type}
                      </span>
                    )}
                    {selectedColor && (
                      <span
                        style={{
                          fontSize: 10,
                          fontFamily: "'Inter', sans-serif",
                          color: "hsl(var(--muted-foreground))",
                          marginLeft: product.machine_type ? 6 : 0,
                        }}
                      >
                        Color: {selectedColor}
                      </span>
                    )}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 8,
                      }}
                    >
                      {/* Qty controls */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 8,
                          padding: "2px 4px",
                        }}
                      >
                        <button
                          onClick={() =>
                            dispatch(updateQuantity({ productId: pid, quantity: quantity - 1 }))
                          }
                          aria-label="Decrease quantity"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "2px 4px",
                            display: "flex",
                            alignItems: "center",
                            color: "hsl(var(--muted-foreground))",
                          }}
                        >
                          <Minus size={11} />
                        </button>
                        <span
                          style={{
                            fontFamily: "'DM Mono', monospace",
                            fontSize: 12,
                            fontWeight: 600,
                            minWidth: 16,
                            textAlign: "center",
                          }}
                        >
                          {quantity}
                        </span>
                        <button
                          onClick={() =>
                            dispatch(updateQuantity({ productId: pid, quantity: quantity + 1 }))
                          }
                          aria-label="Increase quantity"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "2px 4px",
                            display: "flex",
                            alignItems: "center",
                            color: "hsl(var(--muted-foreground))",
                          }}
                        >
                          <Plus size={11} />
                        </button>
                      </div>

                      {/* Price + remove */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span
                          style={{
                            fontFamily: "'DM Mono', monospace",
                            fontSize: 13,
                            fontWeight: 700,
                            color: "var(--vb-indigo)",
                          }}
                        >
                          ${fmtPrice(price * quantity)}
                        </span>
                        <button
                          onClick={() => dispatch(removeItem(pid))}
                          aria-label="Remove item"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "hsl(var(--muted-foreground))",
                            display: "flex",
                            alignItems: "center",
                            padding: 2,
                          }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer — totals + CTAs */}
        {items.length > 0 && (
          <div
            style={{
              padding: "16px 20px",
              borderTop: "1px solid hsl(var(--border))",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              flexShrink: 0,
            }}
          >
            {/* Subtotal */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 13,
                  color: "hsl(var(--muted-foreground))",
                }}
              >
                Subtotal
              </span>
              <span
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 16,
                  fontWeight: 700,
                  color: "hsl(var(--foreground))",
                }}
              >
                ${fmtPrice(total)}
              </span>
            </div>
            <p
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 11,
                color: "hsl(var(--muted-foreground))",
                margin: 0,
              }}
            >
              Shipping & taxes calculated at checkout
            </p>

            {/* CTAs */}
            <button
              onClick={handleCheckout}
              style={{
                background: "var(--grad-primary)",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                padding: "13px 0",
                fontSize: 14,
                fontWeight: 700,
                fontFamily: "'Inter', sans-serif",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                width: "100%",
                transition: "opacity 0.15s",
              }}
            >
              Checkout <ArrowRight size={14} />
            </button>
            <button
              onClick={handleViewCart}
              style={{
                background: "hsl(var(--secondary))",
                color: "hsl(var(--foreground))",
                border: "1px solid hsl(var(--border))",
                borderRadius: 12,
                padding: "11px 0",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                cursor: "pointer",
                width: "100%",
                transition: "background 0.15s",
              }}
            >
              View Cart
            </button>
          </div>
        )}
      </div>
    </>
  );
}
